import React, { useEffect } from "react";
import {
  Button,
  ButtonGroup,
  Col,
  Container,
  Dropdown,
  DropdownButton,
  Form,
  OverlayTrigger,
  Popover,
  Row,
  ToggleButton,
} from "react-bootstrap";
import styled from "styled-components";
import { ChromePicker } from "react-color";
import useSignatureStyle from "./store/signatureStyle";
import {
  FontList,
  INITIAL_SIGNATURE_STYLES,
  MOCK_SIGNATURE,
  SIGNATURE_IMAGE_CONFIG,
} from "./store/literal";
import { Preview } from "./Preview";
import { SignatureStyleFields, SignatureStyles } from "./store/types";
import ReactDOMServer from "react-dom/server";
import * as API from "./../../helpers/api";
import { cloneDeep } from "lodash";

const SettingContainer = styled.div`
  max-height: 650px;
  overflow-y: scroll;
`;

const PreviewContainer = styled.div`
  display: flex;
  background-color: lightgray;
  justify-content: center;
  align-items: center;
  padding: 80px;
  min-height: 700px;
`;

export const SignatureDesign = () => {
  const { signatureStyle } = useSignatureStyle();
  const [isSaving, setIsSaving] = React.useState<boolean>(false);
  const [btnText, setBtnText] = React.useState<string>("Save Changes");
  const [selectedTenant, setSelectedTenant] =
    React.useState<string>("smoothstack");

  useEffect(() => {
    setBtnText("Save Changes");
  }, [signatureStyle, setBtnText]);

  // Hepler function to convert react component to htmlstring
  const convertHtmlToString = (
    element: React.ReactElement<any, string | React.JSXElementConstructor<any>>
  ) => {
    return ReactDOMServer.renderToString(element);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setBtnText("Saving your changes....");

    const allTenantsRequestData: Record<string, SignatureStyles> = {}; // Object to store request data for all tenants

    for (const tenant of Object.keys(signatureStyle)) {
      const { picH, linkH } = SIGNATURE_IMAGE_CONFIG;

      const signatureLayout = ReactDOMServer.renderToString(
        <Preview
          data={MOCK_SIGNATURE}
          signatureStyle={signatureStyle[tenant]}
          selectedTenant={selectedTenant}
          isGenerateSignatureFrame={true}
        />
      );

      const profileImageSection = convertHtmlToString(
        <td>
          <div
            style={{
              height: `${picH}px`,
              maxWidth: `${picH}px`,
              marginRight: "5px",
              overflow: "hidden",
            }}
          >
            <img
              src="[INSERT PROFILE_URL]"
              alt=""
              height={`${picH}`}
              width={`${picH}`}
              style={{
                height: `${picH}px`,
                width: `${picH}px`,
              }}
            />
          </div>
        </td>
      );

      const phoneNumberSection = convertHtmlToString(
        <span>[INSERT PHONE_NUMBER]</span>
      );

      const companyWebsiteSection = convertHtmlToString(
        <span>
          <a href="[INSERT COMPANY_WEBSITE_URL]" target="_blank">
            [INSERT COMPANY_WEBSITE_URL_LABEL]
          </a>
        </span>
      );

      const landlinePhoneNumberSection = convertHtmlToString(
        <>
          <span>[INSERT LANDLINE_PHONE_NUMBER] | Landline</span>
        </>
      );

      const textPhoneNumberSection = convertHtmlToString(
        <>
          <span>[INSERT TEXT_PHONE_NUMBER] | Text Only</span>
        </>
      );

      const addressSection = convertHtmlToString(
        <>
          <span>[INSERT MAILING_ADDRESS]</span>
        </>
      );

      const companyLogoSection = convertHtmlToString(
        <a href="[INSERT COMPANY_WEBSITE_URL]" target="_blank" rel="noreferrer">
          <img
            src="[INSERT COMPANY_LOGO_URL]"
            alt=""
            height={`${signatureStyle[tenant].companyLogoSize}`}
            style={{
              height: `${signatureStyle[tenant].companyLogoSize}px`,
            }}
          />
        </a>
      );

      const badgeSection = convertHtmlToString(
        <img
          src="[INSERT BADGE_URL]"
          alt=""
          height={`${signatureStyle[tenant].badgeSize}`}
          width={`${signatureStyle[tenant].badgeSize}`}
          style={{
            height: `${signatureStyle[tenant].badgeSize}px`,
            width: `${signatureStyle[tenant].badgeSize}px`,
            marginLeft: "1px",
            marginBottom: "1px",
          }}
        />
      );

      const calendarSectionTop = convertHtmlToString(
        <div style={{ height: `${linkH}px` }}>
          <span>
            <a href="[INSERT CALENDAR_URL]" target="_blank" rel="noreferrer">
              [INSERT CALENDAR_URL_LABEL]
            </a>
          </span>
        </div>
      );

      const calendarSection = convertHtmlToString(
        <span>
          <a href="[INSERT CALENDAR_URL]" target="_blank" rel="noreferrer">
            [INSERT CALENDAR_URL_LABEL]
          </a>
        </span>
      );

      const signatureHtml = {
        signatureLayout,
        profileImageSection,
        phoneNumberSection,
        companyWebsiteSection,
        addressSection,
        companyLogoSection,
        badgeSection,
        calendarSection,
        calendarSectionTop,
        textPhoneNumberSection,
        landlinePhoneNumberSection,
      };

      allTenantsRequestData[tenant] = {
        ...signatureStyle[tenant],
        sections: signatureHtml,
      };
    }

    try {
      await API.saveSignatureConfigData(allTenantsRequestData); // Save all tenants' data
      setIsSaving(false);
      setBtnText("Saved");
    } catch {
      setIsSaving(false);
      setBtnText("Something went wrong, please save it again!");
    }
  };

  const previewData = { ...MOCK_SIGNATURE };

  return (
    <div>
      <Container>
        <Row>
          <Col md={4}>
            <DesignSection
              selectedTenant={selectedTenant}
              setSelectedTenant={setSelectedTenant}
              handleSave={handleSave}
              btnText={btnText}
              isSaving={isSaving}
            />
          </Col>
          <Col md={8}>
            <PreviewContainer>
              <Container>
                <Row>
                  <strong>Signature Preview</strong>
                </Row>
                {/* Actual Signature Starting Point */}
                <Preview
                  data={previewData}
                  signatureStyle={signatureStyle[selectedTenant]}
                  selectedTenant={selectedTenant}
                />
              </Container>
            </PreviewContainer>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

const DesignSection: React.FC<{
  selectedTenant: string;
  setSelectedTenant: React.Dispatch<React.SetStateAction<string>>;
  handleSave: () => void;
  isSaving: boolean;
  btnText: string;
}> = ({ handleSave, btnText, isSaving, selectedTenant, setSelectedTenant }) => {
  const {
    signatureStyle,
    updateStyle,
    updateSubStyle,
    setStyle,
    loadTenantStyle,
    setInitialSignatureStyle,
  } = useSignatureStyle();

  const tenants = ["smoothstack", "fedstack", "uptimecrew"];
  const tenantTitles = {
    smoothstack: "Smoothstack",
    fedstack: "Fedstack",
    uptimecrew: "Uptime Crew",
  };

  // Handle tenant change
  const handleTenantChange = (tenant: string) => {
    setSelectedTenant(tenant);
    const tenantStyle = loadTenantStyle(tenant);

    if (!tenantStyle) {
      // If no style exists for the tenant, initialize it
      setInitialSignatureStyle(tenant, INITIAL_SIGNATURE_STYLES[tenant]);
    }
  };

  const handleSetToDefault = (tenant: string) => {
    const defaultStyle = cloneDeep(INITIAL_SIGNATURE_STYLES[tenant]);

    setStyle((draft) => {
      if (draft[tenant]) {
        draft[tenant] = {
          ...defaultStyle,
          mailingAddress: draft[tenant].mailingAddress, // Preserve mailing address if needed
        };
      }
    });
  };

  /**
   * Color popover to allow user to select a color or enter color code
   * @param field
   * @returns
   */
  const colorPopover = (tenant: string, field: SignatureStyleFields) => {
    return (
      <Popover id="popover-basic">
        <ChromePicker
          color={signatureStyle[tenant][field].color}
          onChangeComplete={(color) =>
            updateSubStyle(selectedTenant, field, "color", color.hex)
          }
        />
      </Popover>
    );
  };

  /**
   * This is the generic function to generate styling design for font, size, bold, and color
   * Currently it is taking fields including: employeeFirstName, employeeLastName, title, additionalFields
   * @param field
   * @returns
   */
  const DesignSubsection = (field: SignatureStyleFields) => {
    return (
      <>
        <Container>
          <Row>
            <Col md={3}>Font</Col>
            <Col md={9}>
              <DropdownButton
                id="dropdown-basic-button"
                title={signatureStyle[selectedTenant][field].font}
                size="sm"
              >
                {FontList.map((f) => (
                  <Dropdown.Item
                    onClick={() =>
                      updateSubStyle(selectedTenant, field, "font", f)
                    }
                  >
                    <span style={{ fontFamily: f }}>{f}</span>
                  </Dropdown.Item>
                ))}
              </DropdownButton>
            </Col>
          </Row>
          <br />
          <Row>
            <Col md={3}>Color</Col>
            <Col md={9}>
              <OverlayTrigger
                trigger="click"
                placement="bottom"
                overlay={colorPopover(selectedTenant, field)}
              >
                <Button
                  size="sm"
                  style={{
                    background: signatureStyle[selectedTenant][field].color,
                  }}
                >
                  {signatureStyle[selectedTenant][field].color}
                </Button>
              </OverlayTrigger>
            </Col>
          </Row>
          <br />
          <Row>
            <Col md={3}>Weight</Col>
            <Col md={9}>
              <ButtonGroup>
                <ToggleButton
                  key={`radio-${field}-1`}
                  id={`radio-${field}-1`}
                  type="checkbox"
                  variant={"outline-primary"}
                  name="checkbox"
                  value={"normal"}
                  size="sm"
                  checked={
                    signatureStyle[selectedTenant][field].weight === "normal"
                  }
                  onChange={() =>
                    updateSubStyle(selectedTenant, field, "weight", "normal")
                  }
                >
                  Regular
                </ToggleButton>
                <ToggleButton
                  key={`radio-${field}-2`}
                  id={`radio-${field}-2`}
                  type="checkbox"
                  variant={"outline-primary"}
                  name="checkbox"
                  value={"bold"}
                  size="sm"
                  checked={
                    signatureStyle[selectedTenant][field].weight === "bold"
                  }
                  onChange={() =>
                    updateSubStyle(selectedTenant, field, "weight", "bold")
                  }
                >
                  <strong>Bold</strong>
                </ToggleButton>
              </ButtonGroup>
            </Col>
          </Row>
          <br />
          <Row>
            <Col
              md={3}
            >{`Size (${signatureStyle[selectedTenant][field].size}px)`}</Col>
            <Col md={9}>
              <Form.Range
                onChange={(e) => {
                  updateSubStyle(
                    selectedTenant,
                    field,
                    "size",
                    +e.target.value / 2
                  );
                }}
                value={signatureStyle[selectedTenant][field].size * 2}
              />
            </Col>
          </Row>
          <br />
        </Container>
        <hr />
      </>
    );
  };

  return (
    <div>
      <SettingContainer>
        {/* Tenant Dropdown */}
        <div>
          <p>
            <strong>Select Tenant</strong>
          </p>
          <DropdownButton
            id="tenant-dropdown"
            title={tenantTitles[selectedTenant as keyof typeof tenantTitles]}
            onSelect={(tenant) => handleTenantChange(tenant!)}
          >
            {tenants.map((tenant) => (
              <Dropdown.Item key={tenant} eventKey={tenant}>
                {tenantTitles[tenant as keyof typeof tenantTitles]}
              </Dropdown.Item>
            ))}
          </DropdownButton>
        </div>
        <br />
        {/* Input Box Sections */}
        <div>
          {[
            {
              label: "Company Logo Url",
              field: "companyLogoUrl",
              value: signatureStyle[selectedTenant].companyLogoUrl,
            },
            {
              label: "Company Website Url Label",
              field: "companyWebsiteUrlLabel",
              value: signatureStyle[selectedTenant].companyWebsiteUrlLabel,
              isDisabled: true,
            },
            {
              label: "Company Website Url",
              field: "companyWebsiteUrl",
              value: signatureStyle[selectedTenant].companyWebsiteUrl,
              isDisabled: true,
            },
            {
              label: "Company Mailing Address",
              field: "mailingAddress",
              value: signatureStyle[selectedTenant].mailingAddress,
            },
            // {
            //   label: "Profile Image Defualt Url",
            //   field: "profileDefaultUrl",
            //   value: signatureStyle.profileDefaultUrl,
            // },
          ].map((item, index) => {
            return (
              <p tabIndex={index}>
                <strong>{item.label}</strong>
                <Form.Control
                  value={item.value || ""}
                  onChange={(e) => {
                    updateStyle(
                      selectedTenant,
                      item.field as keyof SignatureStyles,
                      e.target.value
                    );
                  }}
                  disabled={!!item.isDisabled}
                />
              </p>
            );
          })}
        </div>
        {/* Stylling Sections that use DesignSubsection() */}
        <div>
          {[
            { label: "Employee First Name", fieldName: "employeeFirstName" },
            { label: "Employee Last Name", fieldName: "employeeLastName" },
            { label: "Title", fieldName: "title" },
            { label: "Additional Fields", fieldName: "additionalFields" },
          ].map((item, index) => {
            return (
              <div tabIndex={index}>
                <p>
                  <strong>{item.label}</strong>
                </p>
                {DesignSubsection(item.fieldName as SignatureStyleFields)}
              </div>
            );
          })}
        </div>
        <div>
          {[
            {
              label: "Badge Size",
              field: "badgeSize",
              value: signatureStyle[selectedTenant].badgeSize,
            },
            {
              label: "Company Logo Size",
              field: "companyLogoSize",
              value: signatureStyle[selectedTenant].companyLogoSize,
            },
          ].map((item, index) => {
            return (
              <div>
                <p>
                  <strong>{item.label}</strong>
                </p>
                <Container>
                  <Row>
                    <Col md={3}>{`Size (${item.value}px)`}</Col>
                    <Col md={9}>
                      <Form.Range
                        onChange={(e) => {
                          const size = +e.target.value;
                          if (size >= 10 && size <= 40)
                            updateStyle(
                              selectedTenant,
                              item.field as keyof SignatureStyles,
                              e.target.value
                            );
                        }}
                        value={+item.value}
                      />
                    </Col>
                  </Row>
                </Container>
                <hr />
              </div>
            );
          })}
        </div>
      </SettingContainer>
      <br />
      <Button onClick={() => handleSave()} disabled={isSaving}>
        {btnText}
      </Button>
      <Button
        variant="link"
        onClick={() => handleSetToDefault(selectedTenant)}
        disabled={isSaving}
        style={{ textDecoration: "none" }}
      >
        <span className="gradient-text">Reset to Default</span>
      </Button>
    </div>
  );
};
